import { useState, useEffect } from 'react';
import { useTenants } from '../hooks/useTenants';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

interface SuperAdminTenant {
  id: string;
  name: string;
  slug: string;
  contactEmail?: string | null;
  active: boolean;
}

export function TenantSelector() {
  const [isOpen, setIsOpen] = useState(false);
  const { tenants, currentTenant, loading, switchTenant } = useTenants();
  const { user } = useAuth();

  // States for SUPERADMIN mode
  const [allTenants, setAllTenants] = useState<SuperAdminTenant[]>([]);
  const [selectedSuperAdminTenant, setSelectedSuperAdminTenant] = useState<string>('');
  const [loadingSuperAdmin, setLoadingSuperAdmin] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const isSuperAdmin = user?.role === 'SUPERADMIN';

  // Ordem alfabética; sem busca = 5 primeiras (mais a selecionada se não estiver nas 5); com busca = todas que batem (nome, slug ou email)
  const sortedTenants = [...allTenants].sort((a, b) =>
    (a.name || '').localeCompare(b.name || '', 'pt-BR', { sensitivity: 'base' })
  );
  const query = searchQuery.trim().toLowerCase();
  const filteredTenants = !isSuperAdmin
    ? sortedTenants
    : query === ''
      ? (() => {
          const first5 = sortedTenants.slice(0, 5);
          const selectedId = selectedSuperAdminTenant;
          if (!selectedId || first5.some((t) => t.id === selectedId)) return first5;
          const selected = sortedTenants.find((t) => t.id === selectedId);
          return selected ? [selected, ...first5.filter((t) => t.id !== selectedId)].slice(0, 6) : first5;
        })()
      : sortedTenants.filter(
          (t) =>
            (t.name && t.name.toLowerCase().includes(query)) ||
            (t.slug && t.slug.toLowerCase().includes(query)) ||
            (t.contactEmail && t.contactEmail.toLowerCase().includes(query))
        );

  // Load all tenants for SUPERADMIN
  useEffect(() => {
    if (isSuperAdmin) {
      loadAllTenants();
    }
  }, [isSuperAdmin]);

  const authenticatedFetch = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    return fetch(url, {
      ...options,
      headers,
    });
  };

  const loadAllTenants = async () => {
    try {
      setLoadingSuperAdmin(true);
      // SUPERADMIN: listar todas as empresas (GET /api/tenants), sem filtrar por usuário/ativo
      const response = await authenticatedFetch('/api/tenants');
      if (response.ok) {
        const data = await response.json();
        const raw = data.tenants || [];
        const tenantsArray: SuperAdminTenant[] = raw.map((t: Record<string, unknown>) => ({
          id: t.id as string,
          name: t.name as string,
          slug: t.slug as string,
          contactEmail: (t.contactEmail ?? t.contact_email) ?? null,
          active: t.active !== false
        }));
        setAllTenants(tenantsArray);

        const savedTenantId = localStorage.getItem('superadmin_selected_tenant');
        if (savedTenantId && tenantsArray.some((t: SuperAdminTenant) => t.id === savedTenantId)) {
          setSelectedSuperAdminTenant(savedTenantId);
        } else {
          setSelectedSuperAdminTenant('');
        }
      }
    } catch (error) {
      console.error('Erro ao carregar empresas:', error);
      toast.error('Erro ao carregar empresas');
    } finally {
      setLoadingSuperAdmin(false);
    }
  };

  const handleSuperAdminTenantSwitch = async (tenantId: string) => {
    try {
      setIsOpen(false);
      setLoadingSuperAdmin(true);

      const token = localStorage.getItem('auth_token');
      const response = await authenticatedFetch('/api/user-tenants/switch', {
        method: 'POST',
        body: JSON.stringify({ tenantId })
      });

      if (!response.ok) {
        throw new Error('Erro ao trocar empresa');
      }

      const data = await response.json();
      if (data.success) {
        // Update token with new tenant context
        localStorage.setItem('auth_token', data.token);
        localStorage.setItem('superadmin_selected_tenant', tenantId);
        localStorage.setItem('selected_tenant_id', tenantId); // Sincronizar com TenantContext

        toast.success('Empresa alterada com sucesso');

        // Reload page to refresh all tenant-specific data
        window.location.reload();
      }
    } catch (error) {
      console.error('Erro ao trocar empresa:', error);
      toast.error('Erro ao trocar empresa');
      setLoadingSuperAdmin(false);
    }
  };

  // Limpar seleção de tenant (voltar ao dashboard)
  const handleClearSelection = () => {
    setIsOpen(false);
    setSelectedSuperAdminTenant('');
    localStorage.removeItem('superadmin_selected_tenant');
    localStorage.removeItem('selected_tenant_id');
    toast.success('Voltando ao Dashboard');
    window.location.href = '/superadmin/dashboard';
  };

  // SUPERADMIN mode
  if (isSuperAdmin) {
    const selectedTenant = allTenants.find(t => t.id === selectedSuperAdminTenant);

    return (
      <div className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center space-x-2 px-3 py-2 rounded-lg border border-blue-300 bg-blue-50 hover:bg-blue-100 transition-colors"
          disabled={loadingSuperAdmin}
        >
          <svg className="h-4 w-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 8v-3a1 1 0 011-1h2a1 1 0 011 1v3" />
          </svg>
          <span className="text-sm text-blue-700 font-medium max-w-[150px] truncate">
            {loadingSuperAdmin ? 'Carregando...' : (selectedTenant?.name || 'Selecionar empresa')}
          </span>
          <svg className={`h-4 w-4 text-blue-600 transform transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {isOpen && (
          <>
            <div className="fixed inset-0 z-10 bg-black/5" onClick={() => setIsOpen(false)} />
            <div className="absolute right-0 mt-1 w-72 bg-white rounded-lg shadow-lg border border-gray-200 z-20">
              <div className="py-1">
                <div className="px-3 py-2 text-xs font-medium text-blue-600 uppercase tracking-wider border-b border-gray-100">
                  🔧 Modo Super Admin
                </div>

                {/* Voltar ao Dashboard - sempre visível */}
                <button
                  onClick={handleClearSelection}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-purple-50 transition-colors text-purple-700 border-b border-gray-100"
                >
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                    </svg>
                    <span className="font-medium">Voltar ao Dashboard</span>
                  </div>
                </button>

                {/* Campo de busca (nome ou email) - evita fechar dropdown ao clicar */}
                <div className="px-2 py-2 border-b border-gray-100" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="text"
                    placeholder="Buscar por nome ou email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onMouseDown={(e) => e.stopPropagation()}
                    className="w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Lista com altura limitada e scroll */}
                <div className="max-h-56 overflow-y-auto py-1">
                  {loadingSuperAdmin ? (
                    <div className="px-3 py-4 text-center text-sm text-gray-500">
                      Carregando empresas...
                    </div>
                  ) : allTenants.length === 0 ? (
                    <div className="px-3 py-4 text-center text-sm text-gray-500">
                      Nenhuma empresa disponível
                    </div>
                  ) : filteredTenants.length === 0 ? (
                    <div className="px-3 py-4 text-center text-sm text-gray-500">
                      Nenhuma empresa encontrada para &quot;{searchQuery.trim()}&quot;
                    </div>
                  ) : (
                    <>
                      {query === '' && allTenants.length > 5 && (
                        <div className="px-3 py-1.5 text-xs text-gray-500 border-b border-gray-100">
                          Mostrando 5 primeiras. Busque para ver todas.
                        </div>
                      )}
                      {filteredTenants.map((tenant) => (
                        <button
                          key={tenant.id}
                          onClick={() => handleSuperAdminTenantSwitch(tenant.id)}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${
                            tenant.id === selectedSuperAdminTenant ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                          }`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="min-w-0">
                              <div className="font-medium truncate">{tenant.name}</div>
                              <div className="text-xs text-gray-500 truncate">
                                {tenant.contactEmail || tenant.slug}
                              </div>
                            </div>
                            {tenant.id === selectedSuperAdminTenant && (
                              <div className="w-2 h-2 bg-blue-500 rounded-full shrink-0" />
                            )}
                          </div>
                        </button>
                      ))}
                    </>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  // Regular user mode
  if (!tenants || tenants.length <= 1) {
    return null;
  }

  const handleTenantSwitch = async (tenantId: string) => {
    setIsOpen(false);
    await switchTenant(tenantId);
  };

  return (
    <div className="relative">
      {/* Tenant Selector Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
        disabled={loading}
      >
        {/* Building icon */}
        <svg className="h-4 w-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 8v-3a1 1 0 011-1h2a1 1 0 011 1v3" />
        </svg>
        <span className="text-sm text-gray-700 max-w-[150px] truncate">
          {currentTenant?.name || 'Selecionar empresa'}
        </span>
        {/* Chevron down icon */}
        <svg className={`h-4 w-4 text-gray-500 transform transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <>
          {/* Overlay */}
          <div
            className="fixed inset-0 z-10 bg-black/5"
            onClick={() => setIsOpen(false)}
          />

          {/* Menu */}
          <div className="absolute right-0 mt-1 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-20">
            <div className="py-1">
              <div className="px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-100">
                Empresas Disponíveis
              </div>

              {tenants.map((tenant) => (
                <button
                  key={tenant.id}
                  onClick={() => handleTenantSwitch(tenant.id)}
                  disabled={loading || tenant.current}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors flex items-center justify-between ${
                    tenant.current ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                  } ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <div>
                    <div className="font-medium">{tenant.name}</div>
                    <div className="text-xs text-gray-500">
                      {tenant.role === 'SUPERADMIN' ? 'Super Admin' : tenant.role}
                    </div>
                  </div>

                  {tenant.current && (
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}